import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Search, Loader2 } from "lucide-react";
import { toast } from 'sonner';
import DoctorSlots from "@/components/DoctorSlots";
import { HospitalStaff, VisitType, PaymentMethod, Appointment } from "@/types/types";
import { appointmentApi } from "@/api/appointment";
import { doctorApi } from "@/api/doctor";
import { DoctorSearchableSelect } from "@/components/common/DoctorSearchableSelect";
import { ensureDoctorPrefix } from "@/lib/doctorName";
import { useAuth } from "@/contexts/AuthContext";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { leadSourceNeedsDetails } from "@/lib/leadSources";
import { LeadSourceSearchableSelect } from "@/components/common/LeadSourceSearchableSelect";
import { patientNeedsRegistrationSourceCapture } from "@/lib/patientRegistrationSource";
import { useBookingHospitals } from "@/hooks/useBookingHospitals";
import { istDateAndTimeToIso, toIstDateInputValue } from "@/utils/dateUtils";
import { canBackdateOpd as userCanBackdateOpd } from "@/lib/opdBackdateAccess";

const CRM_PREPAID_ROLES = new Set([
  "SALES_PERSON",
  "HOSPITAL_ADMIN",
  "SALES_MANAGER",
  "SUPER_ADMIN",
]);

interface Patient {
    id: string;
    name: string;
    patientUniqueId: string;
    phone: string;
    registrationSource?: string | null;
    registrationSourceDetails?: string | null;
    selfRegistrationPending?: boolean;
}

export default function AddAppointment({
    patientId,
    initialOpen = false,
    onDialogClose,
    /** When false, only the dialog is shown (e.g. CRM auto-open after patient create). Default shows "Add New Appointment" button. */
    showTriggerButton = true,
    /** When set (CRM Converted + OPD Booked flow), eligible roles can record consultation already paid → confirmed + bill on the server. */
    crmOpdBookingLeadId = null,
    onCrmOpdBookingComplete,
    /** Required for SUPER_ADMIN when calling book (hospital scope). */
    bookingHospitalId,
    /** CRM lead's hospital — default picker when booking cross-site in a chain. */
    leadHospitalId,
    /** Optional defaults right after CRM patient create (before patients list refreshes). */
    initialRegistrationSource,
    initialRegistrationSourceDetails,
}: {
    patientId: string;
    /** When true with a non-empty patientId, open the dialog once (e.g. after CRM conversion). */
    initialOpen?: boolean;
    onDialogClose?: () => void;
    showTriggerButton?: boolean;
    crmOpdBookingLeadId?: string | null;
    onCrmOpdBookingComplete?: () => void;
    bookingHospitalId?: string;
    leadHospitalId?: string | null;
    initialRegistrationSource?: string | null;
    initialRegistrationSourceDetails?: string | null;
}) {
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [selectedPatientId, setSelectedPatientId] = useState(patientId || "");
    const didAutoOpenRef = useRef(false);
    const [selectedDoctorId, setSelectedDoctorId] = useState("");
    const [patientSearchQuery, setPatientSearchQuery] = useState("");
    const [visitType, setVisitType] = useState<'OPD' | 'IPD' | 'EMERGENCY' | 'DAY_CARE'>('OPD');
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
    const [selectedSlotId, setSelectedSlotId] = useState<string>("");
    const [partiallyBooked, setPartiallyBooked] = useState(false);
    const [doctorOpdCharge, setSlectedDoctorOpdCharge] = useState(0);
    const [registrationSource, setRegistrationSource] = useState<string>("");
    const [registrationSourceDetails, setRegistrationSourceDetails] = useState<string>("");
    /** Avoid overwriting user edits when patients query refetches. */
    const appliedPatientSourceRef = useRef<string | null>(null);

    const { user, isChainAdmin } = useAuth();
    const canBackdateOpd = userCanBackdateOpd(user, isChainAdmin);
    const {
        hospitals: bookingHospitals,
        needsHospitalPicker,
        defaultHospitalId: defaultBookingHospitalId,
    } = useBookingHospitals();
    const [selectedBookingHospitalId, setSelectedBookingHospitalId] = useState("");
    /** CRM OPD Booked: fee already collected outside THMS? */
    const [consultationAlreadyPaid, setConsultationAlreadyPaid] = useState<"no" | "yes">("no");
    const [prepaidMethod, setPrepaidMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
    const [prepaidAmount, setPrepaidAmount] = useState<string>("");
    const [prepaidNotes, setPrepaidNotes] = useState<string>("");

    const showCrmOpdBookingFields =
        !!user?.role && CRM_PREPAID_ROLES.has(user.role) && visitType === "OPD";

    const showCrmPrepaidSection = !!crmOpdBookingLeadId && showCrmOpdBookingFields;

    /** CRM post-registration OPD booking: online (tele/remote) vs offline (in-person). */
    const [opdBookingMode, setOpdBookingMode] = useState<"ONLINE" | "OFFLINE">("OFFLINE");
    /** CRM OPD: free (zero consult) vs paid. */
    const [opdConsultationTier, setOpdConsultationTier] = useState<"FREE" | "PAID">("PAID");
    const showCrmOpdBookingMode = showCrmOpdBookingFields;
    const showCrmPaidSection = showCrmPrepaidSection && opdConsultationTier === "PAID";

    useEffect(() => {
        if (patientId) {
            setSelectedPatientId(patientId);
        }
    }, [patientId]);

    useEffect(() => {
        if (!initialOpen) {
            didAutoOpenRef.current = false;
            return;
        }
        if (patientId && !didAutoOpenRef.current) {
            didAutoOpenRef.current = true;
            appliedPatientSourceRef.current = null;
            setSelectedPatientId(patientId);
            if (initialRegistrationSource) {
                setRegistrationSource(initialRegistrationSource);
                setRegistrationSourceDetails(initialRegistrationSourceDetails || "");
                appliedPatientSourceRef.current = patientId;
            }
            setConsultationAlreadyPaid("no");
            setPrepaidMethod(PaymentMethod.CASH);
            setPrepaidAmount("");
            setPrepaidNotes("");
            setOpdBookingMode("OFFLINE");
            setOpdConsultationTier("PAID");
            setIsAddDialogOpen(true);
        }
    }, [initialOpen, patientId, initialRegistrationSource, initialRegistrationSourceDetails]);

    useEffect(() => {
        if (visitType !== "OPD") setConsultationAlreadyPaid("no");
    }, [visitType]);

    useEffect(() => {
        if (!showCrmOpdBookingMode) return;
        if (doctorOpdCharge <= 0) {
            setOpdConsultationTier("FREE");
            setConsultationAlreadyPaid("no");
        }
    }, [showCrmOpdBookingMode, doctorOpdCharge]);

    useEffect(() => {
        if (opdConsultationTier === "FREE") {
            setConsultationAlreadyPaid("no");
            setPrepaidAmount("");
        }
    }, [opdConsultationTier]);

    const queryClient = useQueryClient();

    const prepaidFinalParsed = useMemo(() => {
        const n = Number.parseFloat(String(prepaidAmount).replace(/,/g, ""));
        return Number.isFinite(n) && n > 0 ? n : null;
    }, [prepaidAmount]);

    /** When list fee is known and final ≤ list, discount = list − final. */
    const displayedOpdDiscount =
        consultationAlreadyPaid === "yes" &&
        doctorOpdCharge > 0 &&
        prepaidFinalParsed != null &&
        prepaidFinalParsed <= doctorOpdCharge
            ? doctorOpdCharge - prepaidFinalParsed
            : null;

    const effectiveBookingHospitalId =
        selectedBookingHospitalId ||
        defaultBookingHospitalId ||
        bookingHospitalId ||
        user?.hospitalId ||
        "";

    const selectedBookingHospital = bookingHospitals.find(
        (h) => h.id === effectiveBookingHospitalId
    );
    const hospitalName = selectedBookingHospital?.name ?? "Hospital";

    useEffect(() => {
        if (!needsHospitalPicker) return;
        const preferred =
            (leadHospitalId && bookingHospitals.some((h) => h.id === leadHospitalId)
                ? leadHospitalId
                : null) ||
            (bookingHospitalId && bookingHospitals.some((h) => h.id === bookingHospitalId)
                ? bookingHospitalId
                : null) ||
            defaultBookingHospitalId;
        if (preferred) setSelectedBookingHospitalId(preferred);
    }, [
        needsHospitalPicker,
        leadHospitalId,
        bookingHospitalId,
        defaultBookingHospitalId,
        bookingHospitals,
    ]);

    const { data: doctors } = useQuery<HospitalStaff[]>({
        queryKey: ["doctors", effectiveBookingHospitalId],
        queryFn: () => doctorApi.getDoctorsByHospital(effectiveBookingHospitalId || undefined),
        enabled: !!effectiveBookingHospitalId,
    });

    const { data: patients } = useQuery<Patient[]>({
        queryKey: ["patients"],
        queryFn: async () => {
            const response = await api.get('/api/patient/');
            return response.data?.data ?? [];
        },
    });

    // Prefill Registration Source from the selected patient (CRM conversion / auto-open),
    // while still allowing the user to change it before booking.
    useEffect(() => {
        if (!selectedPatientId || !patients?.length) return;
        if (appliedPatientSourceRef.current === selectedPatientId) return;
        const patient = patients.find((p) => p.id === selectedPatientId);
        if (!patient) return;
        appliedPatientSourceRef.current = selectedPatientId;
        setRegistrationSource(patient.registrationSource || "");
        setRegistrationSourceDetails(patient.registrationSourceDetails || "");
    }, [selectedPatientId, patients]);

    const filteredPatients = patients?.filter(patient =>
        patient.name.toLowerCase().includes(patientSearchQuery.toLowerCase()) ||
        patient.patientUniqueId.toLowerCase().includes(patientSearchQuery.toLowerCase()) ||
        patient.phone.includes(patientSearchQuery)
    );

    const selectedPatient = patients?.find((p) => p.id === selectedPatientId);
    const needsRegistrationSourceCapture = patientNeedsRegistrationSourceCapture(selectedPatient);

    const createAppointmentMutation = useMutation({
        mutationFn: async (data: {
            patientId: string;
            doctorId: string;
            phoneNumber: string;
            patientName: string;
            doctorName: string;
            hospitalName: string;
            visitType: 'OPD' | 'IPD' | 'EMERGENCY' | 'DAY_CARE';
            scheduledAt: string;
            selectedSlotId: string;
            partiallyBooked: boolean;
            registrationSource?: string;
            registrationSourceDetails?: string;
            skipSlotBecausePrepaid: boolean;
            crmLeadId?: string;
            opdBookingMode?: "ONLINE" | "OFFLINE";
            opdConsultationTier?: "FREE" | "PAID";
            prepaidConsultation?: {
                paymentMethod: PaymentMethod;
                amount: number;
                notes?: string;
            };
            hospitalId?: string;
        }) => {
            const bookPayload: Record<string, unknown> = {
                patientId: data.patientId,
                doctorId: data.doctorId,
                visitType: data.visitType as VisitType,
                scheduledAt: new Date(data.scheduledAt),
                registrationSource: data.registrationSource,
                registrationSourceDetails: data.registrationSourceDetails,
            };
            if (data.hospitalId) bookPayload.hospitalId = data.hospitalId;
            if (data.crmLeadId) {
                bookPayload.crmLeadId = data.crmLeadId;
                if (data.prepaidConsultation) {
                    bookPayload.prepaidConsultation = data.prepaidConsultation;
                }
                if (data.opdBookingMode) {
                    bookPayload.opdBookingMode = data.opdBookingMode;
                }
                if (data.opdConsultationTier) {
                    bookPayload.opdConsultationTier = data.opdConsultationTier;
                }
            }

            const appointmentResponse = await appointmentApi.bookAppointment(bookPayload as Partial<Appointment>);

            const appointmentId =
                (appointmentResponse.data as { data?: { id?: string } })?.data?.id ||
                (appointmentResponse.data as { id?: string })?.id;

            if (!appointmentId) {
                throw new Error('Failed to get appointment ID from response');
            }

            // Slot occupancy is attached on the server during bookAppointment.

            return appointmentResponse.data;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['appointments'] });
            queryClient.invalidateQueries({ queryKey: ['bills'] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            if (variables.skipSlotBecausePrepaid) {
                toast.success('Appointment confirmed with bill and payment recorded');
            } else {
                toast.success('Appointment created successfully');
            }
            if (crmOpdBookingLeadId) {
                onCrmOpdBookingComplete?.();
            }
            setIsAddDialogOpen(false);
            resetFormFields();
        },
        onError: (error: any) => {
            console.error('Error creating appointment:', error);
            toast.error(error.response?.data?.message || 'Failed to create appointment');
        },
    });

    const handleBookingHospitalChange = (hospitalId: string) => {
        setSelectedBookingHospitalId(hospitalId);
        setSelectedDoctorId("");
        setSelectedDate("");
        setSelectedTimeSlot("");
        setSelectedSlotId("");
        setSlectedDoctorOpdCharge(0);
        setPartiallyBooked(false);
    };

    const handleDoctorChange = (doctorId: string) => {
        setSelectedDoctorId(doctorId);
        const doctor = doctors?.find(d => d.id === doctorId);
        const fee = doctor?.opdCharge?.amount || 0;
        setSlectedDoctorOpdCharge(fee);
        if (consultationAlreadyPaid === "yes" && fee > 0) {
            setPrepaidAmount(String(fee));
        }
    };

    const handleDateChange = (date: string) => {
        setSelectedDate(date);
    };

    const handleSlotSelect = (time: string, slotId: string, isPartiallyBooked: boolean) => {
        setSelectedTimeSlot(time);
        setSelectedSlotId(slotId);
        setPartiallyBooked(isPartiallyBooked);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const scheduledAtIso = istDateAndTimeToIso(selectedDate, selectedTimeSlot);
        if (!scheduledAtIso) {
            toast.error('Please select a valid date and time slot.');
            return;
        }

        if (!canBackdateOpd) {
            const todayIst = toIstDateInputValue(new Date());
            if (selectedDate < todayIst) {
                toast.error('Appointment date must be today or in the future');
                return;
            }
        }

        let skipSlotBecausePrepaid = false;
        let prepaidConsultation:
            | { paymentMethod: PaymentMethod; amount: number; notes?: string }
            | undefined;

        if (showCrmPaidSection && consultationAlreadyPaid === "yes") {
            const amt = Number.parseFloat(String(prepaidAmount).replace(/,/g, ""));
            if (!Number.isFinite(amt) || amt <= 0) {
                toast.error("Enter the final amount the patient paid for this OPD consultation.");
                return;
            }
            skipSlotBecausePrepaid = true;
            prepaidConsultation = {
                paymentMethod: prepaidMethod,
                amount: amt,
                notes: prepaidNotes.trim() || undefined,
            };
        }

        if (needsHospitalPicker && !effectiveBookingHospitalId) {
            toast.error("Please select the hospital for this appointment.");
            return;
        }

        if (user?.role === "SUPER_ADMIN" && !effectiveBookingHospitalId) {
            toast.error("Hospital is required for this booking.");
            return;
        }

        if (needsRegistrationSourceCapture) {
            if (!registrationSource.trim()) {
                toast.error("Please select how this self-registered patient heard about the hospital.");
                return;
            }
            if (
                leadSourceNeedsDetails(registrationSource) &&
                !(registrationSourceDetails || "").trim()
            ) {
                toast.error("Please enter registration source details.");
                return;
            }
        }

        createAppointmentMutation.mutate({
            patientId: selectedPatientId,
            doctorId: selectedDoctorId,
            phoneNumber: patients?.find(p => p.id === selectedPatientId)?.phone || '',
            patientName: patients?.find(p => p.id === selectedPatientId)?.name || '',
            doctorName: ensureDoctorPrefix(doctors?.find(d => d.id === selectedDoctorId)?.name || ''),
            hospitalName: hospitalName || '',
            visitType,
            scheduledAt: scheduledAtIso,
            selectedSlotId,
            partiallyBooked,
            registrationSource: registrationSource || undefined,
            registrationSourceDetails:
                registrationSource && (registrationSourceDetails || "").trim()
                    ? registrationSourceDetails.trim()
                    : undefined,
            skipSlotBecausePrepaid,
            crmLeadId: crmOpdBookingLeadId ?? undefined,
            opdBookingMode: showCrmOpdBookingFields ? opdBookingMode : undefined,
            opdConsultationTier: showCrmOpdBookingFields ? opdConsultationTier : undefined,
            prepaidConsultation,
            hospitalId:
                canBackdateOpd ||
                needsHospitalPicker ||
                user?.role === "SUPER_ADMIN"
                    ? effectiveBookingHospitalId || undefined
                    : undefined,
        });
    };

    const resetFormFields = () => {
        if (!patientId) {
            setSelectedPatientId("");
        }
        setSelectedDoctorId("");
        setPatientSearchQuery("");
        setVisitType('OPD');
        setSelectedDate("");
        setSelectedTimeSlot("");
        setSelectedSlotId("");
        setSlectedDoctorOpdCharge(0);
        setPartiallyBooked(false);
        setRegistrationSource("");
        setRegistrationSourceDetails("");
        setConsultationAlreadyPaid("no");
        setPrepaidMethod(PaymentMethod.CASH);
        setPrepaidAmount("");
        setPrepaidNotes("");
        setOpdBookingMode("OFFLINE");
        if (needsHospitalPicker) {
            const preferred =
                (leadHospitalId && bookingHospitals.some((h) => h.id === leadHospitalId)
                    ? leadHospitalId
                    : null) || defaultBookingHospitalId;
            setSelectedBookingHospitalId(preferred);
        } else {
            setSelectedBookingHospitalId("");
        }
    };

    const handleAddDialogOpen = (open: boolean) => {
        if (open) {
            resetFormFields();
        } else {
            onDialogClose?.();
        }
        setIsAddDialogOpen(open);
    };

    const prepaidSubmitBlocked =
        showCrmPrepaidSection &&
        consultationAlreadyPaid === "yes" &&
        (!String(prepaidAmount).trim() || prepaidFinalParsed == null);

    return (
        <>
            {showTriggerButton ? (
                <Button
                    className="text-white bg-blue-800 hover:bg-blue-700"
                    onClick={() => handleAddDialogOpen(true)}
                >
                    Add New Appointment
                </Button>
            ) : null}

            <Dialog open={isAddDialogOpen} onOpenChange={handleAddDialogOpen}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto space-y-6">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold">Add New Appointment</DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Patient Section */}
                        <div className="space-y-2">
                            <Label className="text-base font-semibold">Search Patient</Label>
                            <div className="relative">
                                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
                                <Input
                                    placeholder="Search patients by name, ID or phone..."
                                    value={patientSearchQuery}
                                    onChange={(e) => setPatientSearchQuery(e.target.value)}
                                    className="pl-8"
                                    disabled={createAppointmentMutation.isPending}
                                />
                            </div>
                            {patientSearchQuery && (
                                <div className="rounded-md border bg-white shadow-sm max-h-48 overflow-y-auto">
                                    {filteredPatients?.map((patient) => (
                                        <div
                                            key={patient.id}
                                            className="cursor-pointer p-2 hover:bg-gray-100 transition"
                                            onClick={() => {
                                                if (!createAppointmentMutation.isPending) {
                                                    appliedPatientSourceRef.current = null;
                                                    setSelectedPatientId(patient.id);
                                                    setPatientSearchQuery("");
                                                    // Default to patient's current registration source, but user can override.
                                                    setRegistrationSource(patient.registrationSource || "");
                                                    setRegistrationSourceDetails(patient.registrationSourceDetails || "");
                                                    appliedPatientSourceRef.current = patient.id;
                                                }
                                            }}
                                        >
                                            <div className="font-medium">{patient.name}</div>
                                            <div className="text-sm text-gray-500">
                                                {patient.phone}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {selectedPatientId && (
                                <div className="text-sm text-green-700 mt-5 p-4 pl-0">
                                    {(() => {
                                        const selectedPatient = patients?.find(p => p.id === selectedPatientId);
                                        return selectedPatient ? (
                                            <>Selected: <strong>{selectedPatient.name}</strong> • <span className="text-gray-600">{selectedPatient.phone}</span></>
                                        ) : null;
                                    })()}
                                </div>
                            )}
                        </div>

                        {needsHospitalPicker && (
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">Hospital</Label>
                                <Select
                                    value={effectiveBookingHospitalId || undefined}
                                    onValueChange={handleBookingHospitalChange}
                                    disabled={createAppointmentMutation.isPending}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select hospital" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {bookingHospitals.map((h) => (
                                            <SelectItem key={h.id} value={h.id}>
                                                {h.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {crmOpdBookingLeadId && (
                                    <p className="text-xs text-muted-foreground">
                                        Choose the hospital unit where the OPD visit will take place. Doctors
                                        listed below are registered at the selected hospital.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Doctor + Visit Type */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">Doctor</Label>
                                <DoctorSearchableSelect
                                    doctors={doctors ?? []}
                                    value={selectedDoctorId}
                                    onValueChange={handleDoctorChange}
                                    disabled={createAppointmentMutation.isPending}
                                    showOpdFee
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">Visit Type</Label>
                                <Select
                                    value={visitType}
                                    onValueChange={(val) => setVisitType(val as typeof visitType)}
                                    disabled={createAppointmentMutation.isPending}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select visit type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="OPD">OPD</SelectItem>
                                        <SelectItem value="IPD">IPD</SelectItem>
                                        <SelectItem value="EMERGENCY">Emergency</SelectItem>
                                        <SelectItem value="DAY_CARE">Day Care</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* OPD Charge (only when visit type is OPD) */}
                        {visitType === 'OPD' && doctorOpdCharge > 0 && (
                            <p className="bg-green-50 text-green-700 p-2 rounded-md text-sm">
                                OPD Charge: ₹{doctorOpdCharge.toLocaleString("en-IN")}
                            </p>
                        )}
                        {visitType === 'OPD' &&
                            doctorOpdCharge <= 0 &&
                            !!selectedDoctorId &&
                            showCrmPrepaidSection && (
                                <p className="bg-amber-50 text-amber-900 p-2 rounded-md text-sm">
                                    Standard OPD fee is not set for this doctor. You can still record prepaid by entering
                                    the final amount collected; the bill will use that amount as the consultation charge.
                                </p>
                            )}

                        {showCrmOpdBookingMode && (
                            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-4 space-y-3">
                                <div>
                                    <Label className="text-base font-semibold text-slate-900">
                                        OPD type
                                    </Label>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Choose whether this is an in-person visit or an online / tele consultation.
                                    </p>
                                </div>
                                <RadioGroup
                                    value={opdBookingMode}
                                    onValueChange={(v) =>
                                        setOpdBookingMode(v as "ONLINE" | "OFFLINE")
                                    }
                                    className="space-y-2"
                                >
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="OFFLINE" id="crm-opd-offline" />
                                        <Label htmlFor="crm-opd-offline" className="font-normal cursor-pointer">
                                            Offline — in-person at the hospital
                                        </Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="ONLINE" id="crm-opd-online" />
                                        <Label htmlFor="crm-opd-online" className="font-normal cursor-pointer">
                                            Online — tele / remote consultation
                                        </Label>
                                    </div>
                                </RadioGroup>
                            </div>
                        )}

                        {showCrmOpdBookingMode && (
                            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-4 space-y-3">
                                <div>
                                    <Label className="text-base font-semibold text-slate-900">
                                        Consultation fee
                                    </Label>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Free OPD generates a zero-value bill when reception confirms the patient.
                                        Paid OPD follows the normal billing flow at reception (or prepaid now if already
                                        collected).
                                    </p>
                                </div>
                                <RadioGroup
                                    value={opdConsultationTier}
                                    onValueChange={(v) =>
                                        setOpdConsultationTier(v as "FREE" | "PAID")
                                    }
                                    className="space-y-2"
                                >
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="FREE" id="crm-opd-free" />
                                        <Label htmlFor="crm-opd-free" className="font-normal cursor-pointer">
                                            Free OPD (₹0 consultation)
                                        </Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="PAID" id="crm-opd-paid" />
                                        <Label htmlFor="crm-opd-paid" className="font-normal cursor-pointer">
                                            Paid OPD
                                            {doctorOpdCharge > 0
                                                ? ` (standard fee ₹${doctorOpdCharge.toLocaleString("en-IN")})`
                                                : ""}
                                        </Label>
                                    </div>
                                </RadioGroup>
                            </div>
                        )}

                        {showCrmPaidSection && (
                            <div className="rounded-md border border-blue-200 bg-blue-50/60 p-4 space-y-3">
                                <div>
                                    <Label className="text-base font-semibold text-slate-900">
                                        Consultation payment (CRM — OPD Booked)
                                    </Label>
                                    <p className="text-sm text-slate-600 mt-1">
                                        If the patient already paid, enter the <strong>final</strong> amount collected
                                        (after any discount on the doctor&apos;s standard OPD fee). We confirm the
                                        appointment, create the OPD bill with list price and discount where applicable,
                                        and mark it paid.
                                    </p>
                                </div>
                                <RadioGroup
                                    value={consultationAlreadyPaid}
                                    onValueChange={(v) => {
                                        const next = v as "no" | "yes";
                                        setConsultationAlreadyPaid(next);
                                        if (next === "yes" && doctorOpdCharge > 0) {
                                            setPrepaidAmount(String(doctorOpdCharge));
                                        }
                                        if (next === "no") {
                                            setPrepaidAmount("");
                                        }
                                    }}
                                    className="space-y-2"
                                >
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="no" id="crm-paid-no" />
                                        <Label htmlFor="crm-paid-no" className="font-normal cursor-pointer">
                                            Not yet paid (collect at reception / bill later)
                                        </Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="yes" id="crm-paid-yes" />
                                        <Label htmlFor="crm-paid-yes" className="font-normal cursor-pointer">
                                            Yes — already paid (enter final amount after any discount)
                                        </Label>
                                    </div>
                                </RadioGroup>
                                {consultationAlreadyPaid === "yes" && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                                        <div className="space-y-2">
                                            <Label className="text-sm">Mode of payment</Label>
                                            <Select
                                                value={prepaidMethod}
                                                onValueChange={(v) => setPrepaidMethod(v as PaymentMethod)}
                                                disabled={createAppointmentMutation.isPending}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value={PaymentMethod.CASH}>Cash</SelectItem>
                                                    <SelectItem value={PaymentMethod.CARD}>Card</SelectItem>
                                                    <SelectItem value={PaymentMethod.UPI}>UPI</SelectItem>
                                                    <SelectItem value={PaymentMethod.BANK_TRANSFER}>
                                                        Bank transfer
                                                    </SelectItem>
                                                    <SelectItem value={PaymentMethod.CHEQUE}>Cheque</SelectItem>
                                                    <SelectItem value={PaymentMethod.INSURANCE}>Insurance</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm">Final amount collected (₹)</Label>
                                            <Input
                                                type="number"
                                                min={0}
                                                step="0.01"
                                                value={prepaidAmount}
                                                onChange={(e) => setPrepaidAmount(e.target.value)}
                                                disabled={createAppointmentMutation.isPending}
                                                placeholder={
                                                    doctorOpdCharge > 0
                                                        ? `e.g. ${doctorOpdCharge} or less if discounted`
                                                        : "Enter amount received"
                                                }
                                            />
                                            {doctorOpdCharge > 0 && (
                                                <p className="text-xs text-slate-600">
                                                    Standard OPD fee for this doctor:{" "}
                                                    <span className="font-semibold tabular-nums">
                                                        ₹{doctorOpdCharge.toLocaleString("en-IN")}
                                                    </span>
                                                    {displayedOpdDiscount != null && displayedOpdDiscount > 0 ? (
                                                        <>
                                                            {" "}
                                                            · Discount applied:{" "}
                                                            <span className="font-semibold text-emerald-800 tabular-nums">
                                                                ₹{displayedOpdDiscount.toLocaleString("en-IN")}
                                                            </span>
                                                        </>
                                                    ) : prepaidFinalParsed != null &&
                                                      prepaidFinalParsed > doctorOpdCharge ? (
                                                        <>
                                                            {" "}
                                                            · Above standard fee (no line discount); bill net matches
                                                            amount entered.
                                                        </>
                                                    ) : null}
                                                </p>
                                            )}
                                        </div>
                                        <div className="space-y-2 sm:col-span-2">
                                            <Label className="text-sm text-muted-foreground">Notes (optional)</Label>
                                            <Input
                                                value={prepaidNotes}
                                                onChange={(e) => setPrepaidNotes(e.target.value)}
                                                placeholder="e.g. receipt number, UPI ref"
                                                disabled={createAppointmentMutation.isPending}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Registration Source override (supersedes patient source + keeps history) */}
                        <div className="space-y-2">
                            <Label className="text-base font-semibold">
                                Registration Source{needsRegistrationSourceCapture ? " *" : ""}
                            </Label>
                            {needsRegistrationSourceCapture ? (
                                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                    This patient self-registered online. Please record how they heard about the
                                    hospital before booking the first visit.
                                </p>
                            ) : (
                                <p className="text-xs text-muted-foreground">
                                    Defaults from the patient (including CRM lead source). Change here if this visit differs.
                                </p>
                            )}
                            <LeadSourceSearchableSelect
                                value={registrationSource}
                                onValueChange={(val) => {
                                    setRegistrationSource(val);
                                    if (!leadSourceNeedsDetails(val)) {
                                        setRegistrationSourceDetails('');
                                    }
                                }}
                                disabled={createAppointmentMutation.isPending}
                                placeholder={
                                    needsRegistrationSourceCapture
                                        ? "Select registration source"
                                        : "Keep existing (optional)"
                                }
                                searchPlaceholder="Search registration source…"
                            />
                            {(leadSourceNeedsDetails(registrationSource)) && (
                                <div className="space-y-2">
                                    <Label className="text-sm text-muted-foreground">
                                        {registrationSource === 'REFERRAL'
                                            ? 'Referral Details'
                                            : registrationSource === 'AFFILIATE'
                                                ? 'Affiliate Details'
                                                : registrationSource === 'OTHER'
                                                    ? 'Other Source'
                                                    : 'Custom Source'}
                                    </Label>
                                    <Input
                                        value={registrationSourceDetails}
                                        onChange={(e) => setRegistrationSourceDetails(e.target.value)}
                                        placeholder={
                                            registrationSource === 'CUSTOM'
                                                ? 'Enter custom source'
                                                : 'Enter details'
                                        }
                                        disabled={createAppointmentMutation.isPending}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Appointment Date */}
                        <div className="space-y-2">
                            <Label className="text-base font-semibold">Appointment Date</Label>
                            <Input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => handleDateChange(e.target.value)}
                                {...(canBackdateOpd
                                    ? {}
                                    : { min: toIstDateInputValue(new Date()) })}
                                disabled={createAppointmentMutation.isPending}
                                required
                            />
                            {canBackdateOpd ? (
                                <p className="text-xs text-muted-foreground">
                                    Chain admin: you can book OPD visits for any date (past, today, or future).
                                </p>
                            ) : null}
                        </div>

                        {/* Slots */}
                        {selectedDoctorId && selectedDate && (
                            <div className="space-y-2">
                                <Label className="text-base font-semibold">Select Time Slot</Label>
                                <DoctorSlots
                                    doctorId={selectedDoctorId}
                                    selectedDate={selectedDate}
                                    hospitalId={effectiveBookingHospitalId || undefined}
                                    onSlotSelect={handleSlotSelect}
                                />
                                {selectedTimeSlot && (
                                    <div className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded-md border border-green-200">
                                        Selected Slot: <span className="font-semibold">{selectedTimeSlot}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsAddDialogOpen(false)}
                                disabled={createAppointmentMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="bg-blue-800 text-white hover:bg-blue-700 disabled:opacity-50"
                                disabled={
                                    !selectedPatientId ||
                                    !selectedDoctorId ||
                                    !selectedDate ||
                                    !selectedTimeSlot ||
                                    prepaidSubmitBlocked ||
                                    createAppointmentMutation.isPending ||
                                    (needsRegistrationSourceCapture && !registrationSource.trim())
                                }
                            >
                                {createAppointmentMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    'Create Appointment'
                                )}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
